<script>
import Cta from 'Atoms/cta'
import { h } from 'vue'

const baseClass = 'cta-label'

export default {
  name: 'CtaLabel',
  components: {
    Cta
  },
  setup(props, { slots }) {
    return () =>
      h(
        Cta,
        {
          ...props,
          baseClass
        },
        slots
      )
  }
}
</script>

<style lang="scss">
@keyframes dot-loader {
  0% {
    transform: scale(0);
  }
  40%,
  100% {
    transform: scale(1);
  }
}

.loading {
  span {
    overflow: visible !important; /* Show overflowing content */
    white-space: normal !important; /* Allow text to wrap */
    text-overflow: clip !important; /* Display full text without truncation */
  }
}

.cta.cta-label {
  transition:
    background $transition,
    color $transition,
    border $transition;
  border-radius: $radius-10;
  min-width: 150px;
  height: 48px;
  padding: 0.75rem 1rem;
  font-weight: $weight-medium;
  letter-spacing: $letter-spacing-cta;
  position: relative;

  span {
    overflow: hidden;
    white-space: normal;
    text-overflow: ellipsis;
  }

  .wrap {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  // Loader
  .cta-loader {
    content: '';
    opacity: 1;
    transition: opacity $transition;
    border-radius: $radius-10;
    width: 100%;
    height: 100%;
    background: inherit;
    position: absolute;
    pointer-events: none;

    &.v-enter-from,
    &.v-leave-to {
      opacity: 0;
    }
  }

  .cta-loader-element {
    $loading-duration: 0.5s;
    top: 50%;
    left: 0;
    right: 0;
    transition: none;
    margin: april-rem(-2) auto auto auto;
    width: april-rem(4);
    height: april-rem(4);
    position: absolute;

    span,
    &::before,
    &::after {
      content: '';
      top: 0;
      transform: scale(0);
      animation: dot-loader $loading-duration $ease-in-quad alternate infinite;
      border-radius: $radius-full;
      width: april-rem(4);
      height: april-rem(4);
      background: currentColor;
      position: absolute;
      transform-origin: 50% 50%;
    }

    &::before {
      left: april-rem(-12);
      animation-delay: calc($loading-duration / 5) * 1;
    }

    span {
      left: 0;
      animation-delay: calc($loading-duration / 5) * 2;
    }

    &::after {
      right: april-rem(-12);
      animation-delay: calc($loading-duration / 5) * 3;
    }
  }

  &.small {
    @include body-text-xs;
    min-width: 100px;
    height: 32px;
    padding: 4px 8px;
    line-height: 24px;

    [class*='ui-icon'],
    .fluid-img {
      width: 12px;
      height: 12px;

      &:first-child:not(:only-child) {
        margin-right: 8px;
      }

      &:last-child:not(:only-child) {
        margin-left: 8px;
      }
    }
  }

  // Icon
  [class*='ui-icon'],
  .fluid-img {
    max-width: 20px;
    height: 20px;

    &:first-child:not(:only-child) {
      margin-right: 12px;
    }

    &:last-child:not(:only-child) {
      margin-left: 12px;
    }
  }

  // Primary
  &.variant-primary {
    background: $color-medium;
    color: $color-white;

    &[aria-current],
    &.current,
    &.active {
      background: $color-medium;
    }

    &:not(span) {
      @include interact {
        background: $color-dark;
        color: $color-white;
      }
    }

    &:disabled:not(.loading),
    &.-disabled {
      background: $color-medium-gray;
      color: $color-bold-gray;

      @include interact {
        background: $color-medium-gray;
        color: $color-bold-gray;
      }
    }
  }

  // Secondary
  &.variant-secondary {
    border: 2px solid $color-medium;
    background: transparent;
    color: $color-medium;

    &[aria-current],
    &.current,
    &.active {
      border-color: $color-medium;
    }

    &:not(span) {
      @include interact {
        color: $color-dark;
        border-color: $color-dark;
      }
    }

    &:disabled:not(.loading),
    &.-disabled {
      border: 2px solid $color-medium-gray;
      background: $color-medium-gray;
      color: $color-bold-gray;

      @include interact {
        border: 2px solid $color-medium-gray;
        background: $color-medium-gray;
        color: $color-bold-gray;
      }
    }

    &.loading {
      color: $color-white;

      .cta-loader-element {
        color: $color-extrabold-gray;
      }
    }
  }

  // Green
  &.variant-green {
    border: 2px solid $color-medium-green;
    background: transparent;
    color: $color-extrabold-gray;

    &[aria-current],
    &.current,
    &.active {
      border-color: $color-medium-green;
    }

    &:not(span) {
      @include interact {
        border-color: $color-medium-green;
      }
    }

    &:disabled:not(.loading),
    &.-disabled {
      border: 2px solid $color-medium-gray;
      background: $color-medium-gray;
      color: $color-bold-gray;

      @include interact {
        border: 2px solid $color-medium-gray;
        background: $color-medium-gray;
        color: $color-bold-gray;
      }
    }
  }

  // Tertiary
  &.variant-tertiary {
    min-width: 0 !important;
    background: rgba($color-white, 0);
    color: $color-medium;

    &[aria-current],
    &.current,
    &.active {
      color: $color-bold-gray;
    }

    &:not(span) {
      @include interact {
        color: $color-dark;
      }
    }

    &:disabled:not(.loading),
    &.-disabled {
      border: 2px solid $color-medium-gray;
      background: $color-medium-gray;
      color: $color-bold-gray;

      @include interact {
        border: 2px solid $color-medium-gray;
        background: $color-medium-gray;
        color: $color-bold-gray;
      }
    }
  }

  // Monochrome
  &.variant-monochrome {
    border: 2px solid $color-white;
    background: transparent;
    color: $color-white;

    &:not(span) {
      @include interact {
        background: $color-white;
        color: $color-dark;
      }
    }

    &:disabled:not(.loading),
    &.-disabled {
      background: $color-medium-gray;
      color: $color-bold-gray;

      @include interact {
        background: $color-medium-gray;
        color: $color-bold-gray;
      }
    }
  }

  // Attention
  &.variant-attention {
    border: 2px solid $color-medium-orange;
    background: $color-medium-orange;
    color: $color-black;

    &:not(span) {
      @include interact {
        border-color: $color-dark-orange;
        background: $color-dark-orange;
        color: $color-black;
      }
    }

    &:disabled:not(.loading),
    &.-disabled {
      border: 2px solid $color-medium-gray;
      background: $color-medium-gray;
      color: $color-bold-gray;

      @include interact {
        border: 2px solid $color-medium-gray;
        background: $color-medium-gray;
        color: $color-bold-gray;
      }
    }
  }

  // Themes
  &.variant-theme-blue {
    background: $color-medium-blue;
  }

  &.variant-theme-green {
    background: $color-medium-green;
  }

  &.variant-theme-blue,
  &.variant-theme-green {
    color: $color-white;

    &[aria-current],
    &.current,
    &.active {
      color: $color-dark-blue;
    }

    &:not(span) {
      @include interact {
        background: $color-dark-blue;
      }
    }

    &:disabled:not(.loading),
    &.-disabled {
      background: $color-medium-gray;
      color: $color-bold-gray;

      @include interact {
        background: $color-medium-gray;
        color: $color-bold-gray;
      }
    }
  }

  &.variant-theme-green {
    color: $color-white;

    &[aria-current],
    &.current,
    &.active {
      color: $color-dark-green;
    }

    &:not(span) {
      @include interact {
        background: $color-dark-green;
      }
    }

    &:disabled:not(.loading),
    &.-disabled {
      background: $color-medium-gray;
      color: $color-bold-gray;

      @include interact {
        background: $color-medium-gray;
        color: $color-bold-gray;
      }
    }
  }

  // Switch
  &[class*='variant-switch'] {
    color: $color-bold-gray;

    &[aria-current],
    &.current,
    &.active {
      background: $color-medium;
      color: $color-white;
    }

    &:not(span) {
      @include interact {
        background: $color-medium;
        color: $color-white;
      }
    }
  }

  &.variant-switch-primary {
    &[aria-current],
    &.current,
    &.active {
      background: var(--theme-color-medium);
    }

    &:not(span) {
      @include interact {
        background: var(--theme-color-medium);
      }
    }
  }

  &.variant-switch-secondary {
    &[aria-current],
    &.current,
    &.active {
      background: var(--theme-color-dark-green);
    }

    &:not(span) {
      @include interact {
        background: var(--theme-color-dark-green);
      }
    }
  }

  &.variant-switch-tertiary {
    &[aria-current],
    &.current,
    &.active {
      background: $color-medium-blue;
    }

    &:not(span) {
      @include interact {
        background: $color-medium-gray;
        color: $color-extrabold-gray;
      }
    }
  }

  &.variant-switch-market {
    &[aria-current],
    &.current,
    &.active {
      background: var(--theme-color-medium);
    }

    &:not(span) {
      @include interact {
        background: var(--theme-color-medium);
      }
    }
  }
}

// hover state if inside a or button
a,
button {
  @include interact {
    .cta-label {
      span {
        text-decoration: none;
      }

      &:not(:disabled):not(.-disabled) {
        // Primary
        &.variant-primary {
          background: $color-dark;
        }
      }

      // Secondary
      &.variant-secondary {
        border-color: $color-dark;
      }

      // Tertiary
      &.variant-tertiary {
        color: $color-dark;
      }

      // Theme
      &.variant-theme-blue {
        background: $color-dark-blue;
      }

      &.variant-theme-green {
        background: $color-dark-green;
      }
    }
  }

  &.fat {
    min-width: april-rem(72);
    height: auto;
    padding: april-rem($spacing-l);

    [class*='ui-icon'],
    .fluid-img {
      max-width: april-rem($spacing-l);
      height: april-rem($spacing-l);
    }
  }
}
</style>
